import * as actionTypes from "./paymentModal.actionTypes";
import { CREDIT_CARD_ERROR, ORDER_SUBMISSION_ERROR } from "../constants";
import { notStartedStep, previousYearStep, stripeStep } from "./paymentModal.constants";
import { SUBMIT_ORDER } from "../order/order.actionTypes";
import { LOCATION_CHANGE } from "react-router-redux";

// phase values: idle | starting | ready | confirming | polling | succeeded | failed
const initialState = {
  dialogOpen: false,
  dialogOpenName: notStartedStep,
  warningDialogOpen: false,
  submitError: "",
  redirect: null,
  loading: false,
  bankValidated: null,
  fullBankAccount: '',
  phase: "idle",
  paymentAttempt: null,
  paymentStatus: null,
  paymentError: null,
  canRetry: false,
  orderSubmitted: false
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.REQUEST_STRIPE_TOKEN:
      return {
        ...state,
        loading: true
      };
    case actionTypes.TOGGLE_DIALOG:
      return {
        ...state,
        submitError: "",
        dialogOpen: !state.dialogOpen,
        dialogOpenName: !state.dialogOpen ? stripeStep : notStartedStep,
        loading: false,
        fullBankAccount: state.fullBankAccount !== 'error' ? state.fullBankAccount : '',
        // Reset payment state machine on every open/close so stale phase never leaks in
        phase: "idle",
        paymentAttempt: null,
        paymentStatus: null,
        paymentError: null,
        canRetry: false
      };
    case actionTypes.SET_PAYMENT_START_META:
      return {
        ...state,
        _pendingStartedAt: action.startedAt
      };
    case actionTypes.SET_PAYMENT_PHASE:
      return {
        ...state,
        phase: action.phase,
        ...(action.phase === "failed" && action.error ? { paymentError: action.error, canRetry: false } : {})
      };
    case actionTypes.TOGGLE_WARNING_DIALOG:
      return {
        ...state,
        warningDialogOpen: !state.warningDialogOpen,
        dialogOpenName: !state.warningDialogOpen ? previousYearStep : notStartedStep
      };
    case actionTypes.OPEN_DIALOG_NAME:
      return {
        ...state,
        dialogOpenName: action.dialogOpenName,
        dialogOpen: false,
        warningDialogOpen: false,
        fullBankAccount: state.fullBankAccount !== 'error' ? state.fullBankAccount : ''
      };
    case actionTypes.STRIPE_PROCESSING_ASYNC.REQUESTED:
      return {
        ...state,
        submitError: "",
        loading: true
      };
    case actionTypes.FETCH_VERIFY_BANK_ASYNC.REQUESTED:
      return {
        ...state,
        bankValidated: null,
        fullBankAccount: ''
      };
    case actionTypes.FETCH_VERIFY_BANK_ASYNC.RECEIVED:
      return {
        ...state,
        bankValidated: action.payload.bankValidated,
        fullBankAccount: action.payload.fullBankAccount,
      };
    case actionTypes.FETCH_VERIFY_BANK_ASYNC.ERROR:
      return {
        ...state,
        bankValidated: null,
        fullBankAccount: 'error',
      };
    case actionTypes.START_PAYMENT_ASYNC.REQUESTED:
      return {
        ...state,
        loading: true,
        submitError: "",
        phase: "starting",
        paymentAttempt: null,
        paymentError: null
      };
    case actionTypes.START_PAYMENT_ASYNC.RECEIVED: {
      const { AttemptId: attemptId, ClientSecret: clientSecret } = action.payload;
      const startedAt = state._pendingStartedAt || null;
      return {
        ...state,
        loading: false,
        phase: "ready",
        paymentAttempt: { attemptId, clientSecret, startedAt },
        _pendingStartedAt: null,
        paymentError: null
      };
    }
    case actionTypes.START_PAYMENT_ASYNC.ERROR:
      return {
        ...state,
        loading: false,
        phase: "failed",
        canRetry: false,
        submitError: action.payload && action.payload.message ? action.payload.message : CREDIT_CARD_ERROR,
        paymentError: { kind: "start", message: action.payload && action.payload.message ? action.payload.message : CREDIT_CARD_ERROR }
      };
    case actionTypes.GET_PAYMENT_STATUS_ASYNC.REQUESTED:
      // Intentionally keep last paymentStatus snapshot visible during polling
      return state;
    case actionTypes.GET_PAYMENT_STATUS_ASYNC.RECEIVED: {
      const status = action.payload;
      console.log("[reducer] GET_PAYMENT_STATUS_ASYNC.RECEIVED", {
        AttemptStatus: status.AttemptStatus,
        OrderStatus: status.OrderStatus,
        IsTerminal: status.IsTerminal,
      }); // CLEANUP
      const nextPhase = status.IsTerminal
        ? (status.OrderStatus === "10" ? "succeeded" : "failed")
        : "polling";
      console.log("[reducer] nextPhase →", nextPhase); // CLEANUP
      return {
        ...state,
        phase: nextPhase,
        paymentStatus: status,
        paymentError: null,
        ...(nextPhase === "failed" ? { canRetry: false } : {})
      };
    }
    case actionTypes.GET_PAYMENT_STATUS_ASYNC.ERROR:
      return {
        ...state,
        paymentError: { kind: "status", message: action.payload && action.payload.message ? action.payload.message : "Status check failed" }
      };
    case actionTypes.CONFIRM_PAYMENT_ASYNC.REQUESTED:
      // No UI change — keep polling phase visible while the round-trip is in flight.
      return state;
    case actionTypes.CONFIRM_PAYMENT_ASYNC.RECEIVED: {
      const confirmStatus = action.payload;
      const confirmedPhase = confirmStatus.OrderStatus === "10" || confirmStatus.PaymentIntentStatus.toLower() === 'succeeded' ? "succeeded" : "failed";
      const confirmedErrorMessage = confirmedPhase === "failed" ? `Payment failed with status: ${confirmStatus.PaymentIntentStatus}` : null;
      return {
        ...state,
        phase: confirmedPhase,
        paymentStatus: {
          AttemptStatus: confirmStatus.AttemptStatus,
          OrderStatus: confirmStatus.OrderStatus,
          IsTerminal: confirmStatus.IsTerminal,
        },
        paymentError: confirmedErrorMessage ? { kind: "confirm", message: confirmedErrorMessage } : null,
      };
    }
    case actionTypes.CONFIRM_PAYMENT_ASYNC.ERROR:
      // Confirm call failed — leave phase as "polling" so usePaymentAttemptPoll keeps running.
      return {
        ...state,
        paymentError: { kind: "confirm", message: action.payload && action.payload.message ? action.payload.message : "Confirm check failed" }
      };
    case SUBMIT_ORDER.RECEIVED:
      return {
        ...state,
        submitError: "",
        dialogOpen: false,
        dialogOpenName: notStartedStep,
        warningDialogOpen: false,
        loading: false,
        orderSubmitted: true
      };
    case actionTypes.STRIPE_PROCESSING_ASYNC.RECEIVED:
      return {
        ...state,
        submitError: "",
        dialogOpen: !state.dialogOpen,
        dialogOpenName: notStartedStep,
        loading: false
      };
    case actionTypes.STRIPE_PROCESSING_ASYNC.ERROR:
      return {
        ...state,
        submitError: CREDIT_CARD_ERROR,
        loading: false
      };
    case SUBMIT_ORDER.ERROR:
      return {
        ...state,
        submitError: ORDER_SUBMISSION_ERROR,
        loading: false,
        orderSubmitted: false
      };
    case actionTypes.SHOW_ERROR:
      return {
        ...state,
        submitError: action.message,
        loading: false
      };
    case actionTypes.SET_REDIRECT:
      return {
        ...state,
        redirect: action.payload
      };
    case LOCATION_CHANGE:
      return {
        ...state,
        redirect: null,
        orderSubmitted: false
      };
    default:
      return state;
  }
}
